import type {
  CarrierDispatchCenter,
  CarrierRateBandRow,
  CarrierServiceAreaRow,
  CarrierTransportServiceRow,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";

type GuidedService = Pick<
  CarrierTransportServiceRow,
  "id" | "carrierId" | "name" | "estimatedDays" | "selfRated"
>;

export function isServiceOffered(
  areas: CarrierServiceAreaRow[],
  service: GuidedService
): boolean {
  return areas.some(
    (area) =>
      area.isActive &&
      area.carriers.some(
        (assignment) =>
          assignment.carrierId === service.carrierId &&
          assignment.active &&
          assignment.serviceCoverages.some(
            (coverage) =>
              coverage.carrierServiceId === service.id && coverage.active
          )
      )
  );
}

function hasRecurringDeliveryRun(center: CarrierDispatchCenter): boolean {
  const schedule = center.runSchedule;
  return (
    schedule !== null &&
    Object.values(schedule.weekly).some((runs) => runs.length > 0)
  );
}

export function ServiceOfferingGuidance({
  service,
  offered,
  labelBands,
  dispatchCenters,
  areaId,
}: {
  service: GuidedService;
  /** Undefined while coverage is unavailable; never treat unknown as missing. */
  offered?: boolean;
  labelBands?: CarrierRateBandRow[];
  dispatchCenters?: CarrierDispatchCenter[];
  areaId?: string;
}) {
  const priced = labelBands?.some(
    (band) =>
      band.carrierServiceId === service.id &&
      (!areaId ||
        band.fromServiceArea.id === areaId ||
        band.toServiceArea.id === areaId)
  );
  const deliveryCenters = dispatchCenters?.filter(
    (center) =>
      center.pickupProviderCarrierId === service.carrierId &&
      center.isActive &&
      center.canHandleDelivery
  );
  const scheduled = deliveryCenters?.some(hasRecurringDeliveryRun);

  return (
    <section
      aria-label={`${service.name} setup guidance`}
      className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm"
    >
      <p className="font-medium">Service setup</p>
      <p className="text-xs text-muted-foreground">
        An active service needs an offering in the relevant canonical Service
        Areas. A quote still depends on the exact lane and selected induction
        plan; schedules and date overrides determine operational feasibility.
        {service.estimatedDays == null
          ? " Delivery speed still needs configuration."
          : service.estimatedDays === 0
            ? service.selfRated
              ? " Zero-day delivery must fit a dispatch run on the induction date."
              : " Zero-day delivery depends on provider handoff and availability on the induction date."
            : " Longer services follow the applicable operating days after induction."}
      </p>
      {(offered === false ||
        (service.selfRated && priced === false) ||
        (service.selfRated && scheduled === false)) && (
        <ul className="space-y-2 text-xs">
          {offered === false && (
            <li className="space-y-1">
              <p>No active area offers this service.</p>
              <Button asChild variant="outline" size="sm-touch">
                <a href="/service-areas">Configure offerings</a>
              </Button>
            </li>
          )}
          {service.selfRated && priced === false && (
            <li className="space-y-1">
              <p>
                {areaId
                  ? "No saved label lane price includes this area."
                  : "No saved label lane price exists for this service."}
              </p>
              <Button asChild variant="outline" size="sm-touch">
                <a href="/pricing">Configure lane prices</a>
              </Button>
            </li>
          )}
          {service.selfRated && deliveryCenters?.length === 0 && (
            <li className="space-y-1">
              <p>No active delivery-capable dispatch center is configured.</p>
              <Button asChild variant="outline" size="sm-touch">
                <a href="/settings/dispatch-centers">
                  Configure dispatch centers
                </a>
              </Button>
            </li>
          )}
          {service.selfRated &&
            deliveryCenters !== undefined &&
            deliveryCenters.length > 0 &&
            scheduled === false && (
              <li className="space-y-1">
                <p>
                  No recurring delivery run is configured on an active dispatch
                  center. Date overrides apply only to their own dates.
                </p>
                <Button asChild variant="outline" size="sm-touch">
                  <a href="/settings/delivery">Configure delivery runs</a>
                </Button>
              </li>
            )}
        </ul>
      )}
    </section>
  );
}
